import { SubCapabilityKey } from "@next-platform/packages-ts-types";
import { useCallback, useState } from "react";
import { useApplicationCapabilitiesAllowed } from "../../../infra/application/capabilities";
import { isRulebookHudSnapshot } from "../selectDisplayedRulebookSnapshot";
import useConversationRulebookSettings from "../useConversationRulebookSettings";
/** A bot message carrying Rulebook meta: that turn evaluated (and so pinned) a Rulebook. */
const hasEvaluatedTurn = (messages) => messages.some((message) => isRulebookHudSnapshot(message["rulebook"]));
/**
 * Whether a chat can still take a Rulebook pin, and its picker's open state. The server pins once (explicitly or on
 * the first turn a Rulebook resolves) and refuses any later change.
 */
export default function useConversationRulebookPin({ conversationId, conversation, altMsgs = [], isAvailable, onNeedsConversation, }) {
    const { settings } = useConversationRulebookSettings({ conversationId });
    // Pinning picks from the user's own Rulebooks, so it needs rulebook.manage too.
    const isAttachAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.RulebookConversationAttach);
    const isManageAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.RulebookManage);
    const [stateIsPickerOpen, setStateIsPickerOpen] = useState(false);
    const isPinned = Boolean(settings.rulebook_id) || hasEvaluatedTurn(conversation) || hasEvaluatedTurn(altMsgs);
    // Visible before the first message too: a conversation only exists once one is sent, so pinning waits for it.
    const isShown = isAvailable && isAttachAllowed && isManageAllowed && !isPinned;
    const openPicker = useCallback(() => {
        if (!conversationId) {
            onNeedsConversation();
            return;
        }
        setStateIsPickerOpen(true);
    }, [conversationId, onNeedsConversation]);
    const closePicker = useCallback(() => setStateIsPickerOpen(false), []);
    return { isShown, isPickerOpen: stateIsPickerOpen && isShown, openPicker, closePicker };
}
